import React, { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { User, Users, Trophy, ChevronRight, Swords, Globe } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { NeonCity } from '../components/stages/NeonCity'
import { FlameKnight } from '../components/characters/FlameKnight'

const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'Play', path: '/play' },
  { label: 'Stages', path: '/stages' },
  { label: 'Characters', path: '/characters' },
  { label: 'Tournaments', path: '/tournaments' },
  { label: 'Profile', path: '/profile' },
]

export function Home() {
  const { state, dispatch } = useApp()
  const navigate = useNavigate()
  const currentUser = state.users.find(u => u.id === state.currentUserId)
  const [activeNav, setActiveNav] = useState('Home')
  const [hoveredMode, setHoveredMode] = useState(null)
  const [isSignUp, setIsSignUp] = useState(false)
  const [formData, setFormData] = useState({ name: '', password: '', tag: '' })
  const [error, setError] = useState('')
  // Show auth form when logged out, show modes when logged in
  const [showAuth, setShowAuth] = useState(!currentUser)

  const handleNavClick = (item) => {
    setActiveNav(item.label)
    if (item.path !== '/') {
      navigate(item.path)
    }
  }

  const handleModeClick = (mode) => {
    if (!state.currentUserId) {
      setShowAuth(true)
    } else {
      navigate('/play')
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    const name = formData.name.trim()
    const password = formData.password.trim()
    const tag = formData.tag.trim()

    if (!name) {
      setError('Enter a name')
      return
    }

    if (!password) {
      setError('Enter a password')
      return
    }

    if (isSignUp) {
      // Check if user exists
      const existing = state.users.find(u => u.name.toLowerCase() === name.toLowerCase())
      if (existing) {
        setError('That name is already taken')
        return
      }

      // Create new user
      const newUser = {
        id: `user_${Date.now()}`,
        name,
        passwordHash: hashPassword(password),
        tag: tag || name,
        createdAt: Date.now(),
      }

      // Add user to users array
      const updatedUsers = [...state.users, newUser]
      dispatch({ type: 'SET_USERS', payload: updatedUsers })
      
      // Set current user
      dispatch({ type: 'SET_CURRENT_USER', payload: newUser.id })
      
      // Component will redirect due to state.currentUserId check above
    } else {
      // Sign in
      const user = state.users.find(u => u.name.toLowerCase() === name.toLowerCase())
      if (!user) {
        setError('User not found. Try signing up?')
        return
      }
      
      // Simple password check
      if (user.passwordHash && user.passwordHash !== hashPassword(password)) {
        setError('Incorrect password')
        return
      }
      
      dispatch({ type: 'SET_CURRENT_USER', payload: user.id })
    }
  }

  // Simple hash function for MVP
  function hashPassword(password) {
    let hash = 0
    for (let i = 0; i < password.length; i++) {
      const char = password.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return hash.toString(16)
  }

  return (
    <div className="relative w-full min-h-screen lg:h-screen bg-gray-950 text-white font-sans selection:bg-cyan-500/30 overflow-y-auto lg:overflow-hidden">
      {/* --- BACKGROUND LAYER --- */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <NeonCity className="w-full h-full object-cover" />
        {/* Dark dramatic gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/90 to-gray-900/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-black/50" />
      </div>

      {/* --- CHARACTER LAYER --- */}
      <div
        className="absolute right-[-5%] bottom-0 w-[40vw] max-w-[400px] h-[80vh] z-0 pointer-events-none opacity-30 lg:opacity-60 animate-fade-in"
        style={{ transform: 'translateZ(0)' }}
      >
        <FlameKnight className="w-full h-full object-contain object-bottom" />
      </div>

      {/* --- UI LAYER --- */}
      <div className="relative z-10 w-full h-full flex flex-col p-6 lg:p-10 max-w-[1600px] mx-auto">
        {/* Top Bar */}
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center w-full mb-8 shrink-0 gap-4">
          <div className="flex flex-col">
            <h1 className="text-4xl md:text-5xl font-black tracking-widest uppercase italic text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 drop-shadow-lg">
              Cache Smash
            </h1>
            <p className="text-cyan-400 text-sm font-bold tracking-widest uppercase mt-1">
              A browser-native platform fighter
            </p>
          </div>

          {/* Player Info Badges */}
          <div className="flex flex-wrap gap-3 text-xs font-bold uppercase tracking-wider">
            {currentUser ? (
              <>
                <div className="flex items-center gap-2 bg-gray-900/80 border border-white/10 px-4 py-2 rounded-full shadow-lg">
                  <User size={14} className="text-cyan-400" />
                  <span>{currentUser.name}</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-900/80 border border-white/10 px-4 py-2 rounded-full shadow-lg hidden sm:flex">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span>Online</span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 bg-gray-900/80 border border-white/10 px-4 py-2 rounded-full shadow-lg">
                <User size={14} className="text-cyan-400" />
                <span>Guest</span>
              </div>
            )}
            <div className="flex items-center gap-2 bg-gray-900/80 border border-white/10 px-4 py-2 rounded-full shadow-lg hidden md:flex">
              <Users size={14} className="text-gray-400" />
              <span>5 Fighters</span>
            </div>
            <div className="flex items-center gap-2 bg-gray-900/80 border border-white/10 px-4 py-2 rounded-full shadow-lg hidden lg:flex">
              <Trophy size={14} className="text-yellow-400" />
              <span>Tournaments</span>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col lg:flex-row gap-8 lg:gap-12 min-h-0">
          {/* Left: Arcade Navigation */}
          <nav
            className="w-full lg:w-64 flex flex-row lg:flex-col gap-2 justify-start shrink-0 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 lg:pt-12"
          >
            {NAV_ITEMS.map((item, index) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item)}
                className={`
                  group relative flex items-center text-left py-3 px-4 transition-colors duration-200 shrink-0 lg:w-full
                  ${activeNav === item.label ? 'text-white' : 'text-gray-500 hover:text-gray-200'}
                `}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Active Indicator */}
                <div
                  className={`
                    absolute left-0 top-0 lg:w-1 lg:h-full h-1 w-full bg-cyan-500
                    ${activeNav === item.label ? 'opacity-100' : 'opacity-0'}
                    lg:origin-left origin-top
                  `}
                />

                <span className="relative z-10 text-xl lg:text-2xl font-black uppercase italic tracking-wider whitespace-nowrap">
                  {item.label}
                </span>

                {activeNav === item.label && (
                  <ChevronRight
                    className="relative z-10 ml-auto text-cyan-500 hidden lg:block"
                    size={24}
                  />
                )}
              </button>
            ))}
          </nav>

          {/* Center: Mode Selection or Auth Form */}
          <div className="flex-1 flex flex-col justify-center max-w-4xl">
            {!showAuth ? (
              <>
                <div className="mb-6 lg:mb-8">
                  <h2 className="text-sm font-bold text-cyan-500 tracking-[0.2em] uppercase mb-2">
                    Welcome
                  </h2>
                  <h3 className="text-4xl lg:text-5xl font-black uppercase italic tracking-tight drop-shadow-lg">
                    Choose a Mode
                  </h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Single Player Card */}
                  <button
                    onMouseEnter={() => setHoveredMode('single')}
                    onMouseLeave={() => setHoveredMode(null)}
                    onClick={() => handleModeClick('single')}
                    className={`
                      relative overflow-hidden rounded-xl border-2 text-left transition-all duration-300
                      ${hoveredMode === 'single' ? 'border-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.2)]' : 'border-white/10 bg-gray-900/60 hover:border-white/30'}
                      aspect-[16/9] flex flex-col justify-end p-6 lg:p-8 group
                    `}
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br from-cyan-600/20 to-blue-900/40 ${hoveredMode === 'single' ? 'opacity-100' : 'opacity-0'}`}
                    />

                    <Swords
                      className={`absolute top-6 right-6 lg:top-8 lg:right-8 w-12 h-12 lg:w-16 lg:h-16 transition-all duration-300 ${hoveredMode === 'single' ? 'text-cyan-400 scale-110' : 'text-gray-600'}`}
                    />

                    <div className="relative z-10">
                      <span className="text-xs font-bold text-cyan-400 tracking-widest uppercase mb-2 block">
                        Single Player
                      </span>
                      <h4 className="text-3xl lg:text-4xl font-black uppercase italic mb-2 lg:mb-3 group-hover:text-cyan-300 transition-colors">
                        Local Match
                      </h4>
                      <p className="text-sm lg:text-base text-gray-400 font-medium max-w-sm group-hover:text-gray-200 transition-colors">
                        Choose both sides, pick a stage, and launch a one-on-one match.
                      </p>
                    </div>
                  </button>

                  {/* Multiplayer Card */}
                  <button
                    onMouseEnter={() => setHoveredMode('multi')}
                    onMouseLeave={() => setHoveredMode(null)}
                    onClick={() => handleModeClick('multi')}
                    className={`
                      relative overflow-hidden rounded-xl border-2 text-left transition-all duration-300
                      ${hoveredMode === 'multi' ? 'border-purple-400 shadow-[0_0_20px_rgba(192,132,252,0.2)]' : 'border-white/10 bg-gray-900/60 hover:border-white/30'}
                      aspect-[16/9] flex flex-col justify-end p-6 lg:p-8 group
                    `}
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br from-purple-600/20 to-pink-900/40 ${hoveredMode === 'multi' ? 'opacity-100' : 'opacity-0'}`}
                    />

                    <Globe
                      className={`absolute top-6 right-6 lg:top-8 lg:right-8 w-12 h-12 lg:w-16 lg:h-16 transition-all duration-300 ${hoveredMode === 'multi' ? 'text-purple-400 scale-110' : 'text-gray-600'}`}
                    />

                    <div className="relative z-10">
                      <span className="text-xs font-bold text-purple-400 tracking-widest uppercase mb-2 block">
                        Multiplayer
                      </span>
                      <h4 className="text-3xl lg:text-4xl font-black uppercase italic mb-2 lg:mb-3 group-hover:text-purple-300 transition-colors">
                        Online Lobby
                      </h4>
                      <p className="text-sm lg:text-base text-gray-400 font-medium max-w-sm group-hover:text-gray-200 transition-colors">
                        Set up a private room, enter an invite code, or join the queue.
                      </p>
                    </div>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-6 lg:mb-8">
                  <button
                    onClick={() => setShowAuth(false)}
                    className="text-sm text-gray-400 hover:text-cyan-400 transition-colors mb-2"
                  >
                    ← Back to Modes
                  </button>
                  <h3 className="text-4xl lg:text-5xl font-black uppercase italic tracking-tight drop-shadow-lg">
                    Sign In to Play
                  </h3>
                </div>

                {/* Auth Form */}
                <div className="bg-gray-900/60 border border-white/10 rounded-2xl p-8">
                  <div className="flex gap-4 mb-6">
                    <button
                      onClick={() => {
                        setIsSignUp(false)
                        setError('')
                      }}
                      className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                        !isSignUp
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-black'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => {
                        setIsSignUp(true)
                        setError('')
                      }}
                      className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                        isSignUp
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-black'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Sign Up
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Fighter Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50"
                        placeholder="Enter your name"
                        maxLength={20}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Password
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50"
                        placeholder="Enter your password"
                      />
                    </div>

                    {isSignUp && (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Tagline (optional)
                        </label>
                        <input
                          type="text"
                          value={formData.tag}
                          onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                          className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50"
                          placeholder="A short tagline"
                          maxLength={30}
                        />
                      </div>
                    )}

                    {error && (
                      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all transform hover:scale-[1.02]"
                    >
                      {isSignUp ? 'Create Account' : 'Sign In'}
                    </button>
                  </form>

                  {/* Reset Data Option */}
                  {state.users.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <button
                        onClick={() => {
                          if (confirm('Clear all saved data and start fresh?')) {
                            localStorage.removeItem('cache-smash-mvp-v1')
                            window.location.reload()
                          }
                        }}
                        className="text-xs text-gray-500 hover:text-red-400 transition-colors"
                      >
                        Reset all data
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bottom Bar: Previews */}
        {!showAuth && (
          <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-end gap-4 shrink-0">
            {/* Fighter Preview */}
            <Link
              to="/characters"
              className="w-full sm:w-48 bg-gray-900/80 border border-white/10 rounded-xl overflow-hidden shadow-2xl group cursor-pointer hover:border-red-500/50 transition-colors flex sm:flex-col"
            >
              <div className="w-24 sm:w-full h-24 sm:h-24 bg-black/50 relative flex items-center justify-center overflow-hidden shrink-0">
                <div className="absolute inset-0 bg-gradient-to-t from-red-900/20 to-transparent" />
                <FlameKnight className="w-16 h-24 sm:w-20 sm:h-28 mt-4 group-hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="p-3 flex-1 flex flex-col justify-center">
                <span className="text-[10px] font-bold text-gray-500 tracking-widest uppercase block mb-1">
                  Roster
                </span>
                <h5 className="font-black uppercase text-sm truncate">
                  5 Fighters
                </h5>
                <button className="mt-2 w-full py-1.5 bg-white/5 hover:bg-white/10 rounded text-xs font-bold uppercase tracking-wider transition-colors">
                  Manage
                </button>
              </div>
            </Link>

            {/* Stage Preview */}
            <Link
              to="/stages"
              className="w-full sm:w-48 bg-gray-900/80 border border-white/10 rounded-xl overflow-hidden shadow-2xl group cursor-pointer hover:border-cyan-500/50 transition-colors flex sm:flex-col"
            >
              <div className="w-24 sm:w-full h-24 sm:h-24 bg-black relative overflow-hidden shrink-0">
                <NeonCity className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="p-3 flex-1 flex flex-col justify-center">
                <span className="text-[10px] font-bold text-gray-500 tracking-widest uppercase block mb-1">
                  Arena
                </span>
                <h5 className="font-black uppercase text-sm truncate">
                  5 Stages
                </h5>
                <button className="mt-2 w-full py-1.5 bg-white/5 hover:bg-white/10 rounded text-xs font-bold uppercase tracking-wider transition-colors">
                  Choose
                </button>
              </div>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
